/**
 * Patient Intakes Tab Component
 * 
 * Shows intake history for a specific patient with ability to send new intakes
 */

import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Send, 
  RefreshCw,
  Loader2,
  Eye,
  Mail,
  Plus,
  Stethoscope
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  getPatientIntakeStatus,
  resendIntakeEmail,
  IntakeStatusItem,
  getIntakeDisplayLabel,
  intakeService,
  InitiateIntakeResponse
} from '@/services/intakeService';
import { useToast } from '@/hooks/use-toast';
import IntakeResponseReviewDrawer from '@/components/clinic/IntakeResponseReviewDrawer';
import IntakeRequestModal, { IntakeRequestConfig } from '@/components/clinic/IntakeRequestModal';
import { GuidedIntakeModal } from '@/components/clinic/GuidedIntakeModal';
import { SendToMdiDialog } from '@/components/clinic/SendToMdiDialog';
import { inferTherapyCategory } from '@/lib/dataCompleteness/requirements';
import { CompletenessResult } from '@/lib/dataCompleteness/types';
import { mdiService } from '@/services/mdiService';
import { useClinic } from '@/contexts/ClinicContext';

interface PatientIntakesTabProps {
  patientId?: string;        // FHIR patient ID for intake service
  patientDbId?: string;      // Database patient ID for MDI service
  patientEmail?: string;
  patientName?: string;
  patientGender?: string;
  /** 'enrollments' = guided intakes only, 'intakes' = non-guided only */
  mode?: 'enrollments' | 'intakes';
}

const PatientIntakesTab: React.FC<PatientIntakesTabProps> = ({
  patientId,
  patientDbId,
  patientEmail,
  patientName,
  patientGender,
  mode,
}) => {
  const { clinic } = useClinic();
  const [isLoading, setIsLoading] = useState(true);
  const [pending, setPending] = useState<IntakeStatusItem[]>([]);
  const [completed, setCompleted] = useState<IntakeStatusItem[]>([]);
  const [reviewed, setReviewed] = useState<IntakeStatusItem[]>([]);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [reviewDrawerOpen, setReviewDrawerOpen] = useState(false);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);
  const [intakeModalOpen, setIntakeModalOpen] = useState(false);
  const [guidedIntakeModalOpen, setGuidedIntakeModalOpen] = useState(false);
  const [isMdiEnabled, setIsMdiEnabled] = useState(false);
  const [mdiCaseResponseIds, setMdiCaseResponseIds] = useState<Set<string>>(new Set());
  const [mdiDialogOpen, setMdiDialogOpen] = useState(false);
  const [selectedMdiIntake, setSelectedMdiIntake] = useState<IntakeStatusItem | null>(null);
  const { toast } = useToast();

  const loadIntakes = async () => {
    if (!patientId) return;
    
    setIsLoading(true);
    try {
      const result = await getPatientIntakeStatus(patientId);
      if (result.success && result.data) {
        setPending(result.data.pending);
        setCompleted(result.data.completed);
        setReviewed(result.data.reviewed);
      }
      
      // Check if MDI is enabled for this clinic
      if (clinic?.id) {
        const mdiEnabled = await mdiService.isEnabled(clinic.id);
        setIsMdiEnabled(mdiEnabled);
        
        // Fetch existing MDI cases to track which intakes already have cases
        if (mdiEnabled && patientDbId) {
          try {
            const casesResult = await mdiService.getPatientCases(patientDbId);
            if (casesResult.success && casesResult.data) {
              // Get questionnaire response IDs that already have MDI cases
              const responseIds = new Set<string>();
              const cases = Array.isArray(casesResult.data) ? casesResult.data : (casesResult.data as any).cases || [];
              cases.forEach((mdiCase: any) => {
                if (mdiCase.questionnaireResponseId || mdiCase.questionnaire_response_id) {
                  responseIds.add(mdiCase.questionnaireResponseId || mdiCase.questionnaire_response_id);
                }
              });
              setMdiCaseResponseIds(responseIds);
            }
          } catch (error) {
            console.error('Error fetching MDI cases:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading intakes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadIntakes();
  }, [patientId, clinic?.id]);

  // Check if an intake already has an MDI case
  const hasExistingMdiCase = (intake: IntakeStatusItem): boolean => {
    const responseId = intake.questionnaireResponseId || intake.questionnaire_response_id || intake.responseId;
    return responseId ? mdiCaseResponseIds.has(responseId) : false;
  };

  const handleResend = async (intakeId: string) => {
    setResendingId(intakeId);
    try {
      const result = await resendIntakeEmail(intakeId);
      if (result.success) {
        toast({
          title: 'Email Sent',
          description: `${formLabelCap} request has been resent to the patient.`,
        });
      } else {
        toast({
          title: 'Failed to Resend',
          description: result.message || 'Could not resend the email.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Failed to Resend',
        description: error?.message || 'An error occurred while resending the email.',
        variant: 'destructive',
      });
    } finally {
      setResendingId(null);
    }
  };

  const handleReviewResponse = (responseId: string) => {
    setSelectedResponseId(responseId);
    setReviewDrawerOpen(true);
  };

  const handleResponseReviewed = () => {
    loadIntakes(); // Refresh the intake list
  };

  // Handle send intake - calls the real API
  const handleSendIntake = async (config: IntakeRequestConfig): Promise<InitiateIntakeResponse> => {
    const response = await intakeService.initiateIntake({
      patientId: config.patientId,
      clinicId: clinic?.id,
      intakeType: config.intakeType,
      therapyCategory: config.therapyCategory,
      patientEmail: config.patientEmail,
      patientName: config.patientName || undefined,
      providerNotes: config.providerNotes || undefined,
      sendEmail: config.sendEmail,
    });
    
    // Refresh intakes list after sending
    if (response.success) {
      loadIntakes();
    }
    
    return response;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Handle opening MDI dialog for manual send
  const handleSendToMdi = (intake: IntakeStatusItem) => {
    setSelectedMdiIntake(intake);
    setMdiDialogOpen(true);
  };

  // Handle MDI send success
  const handleMdiSuccess = () => {
    setMdiDialogOpen(false);
    setSelectedMdiIntake(null);
    loadIntakes(); // Refresh to update MDI case status
    toast({
      title: 'Sent to MDI',
      description: 'The intake has been submitted for async telehealth review.',
    });
  };

  const isRefillIntake = (intake: IntakeStatusItem): boolean => {
    const purpose = ((intake as any).intakePurpose || (intake as any).intake_purpose || '').toString().toLowerCase();
    return purpose === 'refill';
  };

  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const config: Record<string, { label: string; className: string; icon: React.ElementType }> = {
      pending: {
        label: 'Pending',
        className: 'bg-amber-50 text-amber-700 border-amber-200',
        icon: Clock,
      },
      started: {
        label: 'In Progress',
        className: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: RefreshCw,
      },
      completed: {
        label: 'Completed',
        className: 'bg-green-50 text-green-700 border-green-200',
        icon: CheckCircle2,
      },
    };

    const { label, className, icon: Icon } = config[status] || config.pending;

    return (
      <Badge variant="outline" className={className}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const totalIntakes = pending.length + completed.length + reviewed.length;
  const isGuided = clinic?.clinic_archetype === 'guided';
  const isHybrid = clinic?.clinic_archetype === 'hybrid';
  const isEnrollmentMode = mode === 'enrollments' || (!mode && isGuided);
  const formLabel = isEnrollmentMode ? 'enrollment' : 'intake';
  const formLabelCap = isEnrollmentMode ? 'Enrollment' : 'Intake';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{formLabelCap} History</h3>
          <p className="text-sm text-slate-500">
            {totalIntakes} total {formLabel}{totalIntakes !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {patientId && (
            <Button
              size="sm"
              onClick={() => {
                if (isEnrollmentMode && clinic?.id && patientDbId) {
                  setGuidedIntakeModalOpen(true);
                } else {
                  setIntakeModalOpen(true);
                }
              }}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Send {formLabelCap}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={loadIntakes}
            className="border-slate-200"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{pending.length}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{completed.length}</p>
                <p className="text-xs text-slate-500">New Responses</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{reviewed.length}</p>
                <p className="text-xs text-slate-500">Reviewed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Intake List */}
      {totalIntakes === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h4 className="text-lg font-medium text-slate-700 mb-2">No {formLabel}s yet</h4>
            <p className="text-sm text-slate-500 mb-4">
              Send an {formLabel} form to collect patient information
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* New Responses - Highlight */}
          {completed.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                New Responses ({completed.length})
              </h4>
              <div className="space-y-2">
                {completed.map((intake) => (
                  <Card key={intake.id} className="border border-blue-200 bg-blue-50/30">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <ClipboardList className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">
                                {getIntakeDisplayLabel(intake)}
                              </p>
                              {isRefillIntake(intake) && (
                                <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                                  Refill
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">
                              Completed {formatDateTime(intake.completedAt || intake.responseCompletedAt || '')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-700 border-0">New</Badge>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="border-slate-200"
                            onClick={() => {
                              const respId = intake.questionnaireResponseId || intake.questionnaire_response_id || intake.responseId;
                              if (respId) handleReviewResponse(respId);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                          {isMdiEnabled && intake.therapyCategory === 'glp1' && hasExistingMdiCase(intake) && (
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                              <Stethoscope className="w-3 h-3 mr-1" />
                              Sent to MDI
                            </Badge>
                          )}
                          {isMdiEnabled && intake.therapyCategory === 'glp1' && !hasExistingMdiCase(intake) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                              onClick={() => handleSendToMdi(intake)}
                            >
                              <Stethoscope className="w-4 h-4 mr-1" />
                              Send to MDI
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Pending */}
          {pending.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                Pending ({pending.length})
              </h4>
              <div className="space-y-2">
                {pending.map((intake) => (
                  <Card key={intake.id} className="border border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">
                                {getIntakeDisplayLabel(intake)}
                              </p>
                              {isRefillIntake(intake) && (
                                <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                                  Refill
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">
                              Sent {formatDate(intake.createdAt)} · Expires {formatDate(intake.expiresAt)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={intake.status} />
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="border-slate-200"
                            onClick={() => handleResend(intake.id)}
                            disabled={resendingId === intake.id}
                          >
                            {resendingId === intake.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Mail className="w-4 h-4 mr-1" />
                                Resend
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Reviewed */}
          {reviewed.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Reviewed ({reviewed.length})
              </h4>
              <div className="space-y-2">
                {reviewed.map((intake) => (
                  <Card key={intake.id} className="border border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">
                                {getIntakeDisplayLabel(intake)}
                              </p>
                              {isRefillIntake(intake) && (
                                <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                                  Refill
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">
                              Reviewed {intake.reviewedAt ? formatDate(intake.reviewedAt) : 'recently'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-50 text-green-700 border-green-200">Reviewed</Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              const respId = intake.questionnaireResponseId || intake.questionnaire_response_id || intake.responseId;
                              if (respId) handleReviewResponse(respId);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isMdiEnabled && intake.therapyCategory === 'glp1' && hasExistingMdiCase(intake) && (
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                              <Stethoscope className="w-3 h-3 mr-1" />
                              Sent to MDI
                            </Badge>
                          )}
                          {isMdiEnabled && intake.therapyCategory === 'glp1' && !hasExistingMdiCase(intake) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                              onClick={() => handleSendToMdi(intake)}
                            >
                              <Stethoscope className="w-4 h-4 mr-1" />
                              Send to MDI
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Response Review Drawer */}
      <IntakeResponseReviewDrawer
        open={reviewDrawerOpen}
        onOpenChange={setReviewDrawerOpen}
        responseId={selectedResponseId}
        onReviewed={handleResponseReviewed}
        onRunIntelliHealth={() => {
        }}
      />

      {/* Guided Enrollment Modal (guided & hybrid clinics) */}
      {(isGuided || isHybrid) && clinic?.id && patientDbId && (
        <GuidedIntakeModal
          open={guidedIntakeModalOpen}
          onOpenChange={setGuidedIntakeModalOpen}
          clinicId={clinic.id}
          patientDbId={patientDbId}
          patientEmail={patientEmail}
          patientName={patientName}
          onSuccess={loadIntakes}
        />
      )}

      {/* Intake Request Modal (direct clinics) */}
      <IntakeRequestModal
        open={intakeModalOpen}
        onOpenChange={setIntakeModalOpen}
        activeIntakes={pending}
        patient={patientId ? {
          id: patientId,
          first_name: patientName?.split(' ')[0] || '',
          last_name: patientName?.split(' ').slice(1).join(' ') || '',
          full_name: patientName,
          email: patientEmail,
          gender: patientGender,
        } as any : null}
        assessment={{
          therapyCategory: inferTherapyCategory(patientGender || 'male'),
          status: 'insufficient',
          recommendedIntakeType: 'full',
          missingRequired: [],
          missingRecommended: [],
          missingOptional: [],
          presentFields: 0,
          completenessScore: 0,
          totalFields: 0,
          canProceedWithAnalysis: false,
          summary: '',
          actionRequired: 'Requires intake form',
          suggestedQuestions: [],
        }}
        onSendIntake={handleSendIntake}
        onSuccess={loadIntakes}
      />

      {/* Send to MDI Dialog */}
      {selectedMdiIntake && patientDbId && clinic?.id && (
        <SendToMdiDialog
          open={mdiDialogOpen}
          onOpenChange={setMdiDialogOpen}
          patientId={patientDbId}
          patientName={patientName || ''}
          questionnaireResponseId={
            selectedMdiIntake.questionnaireResponseId || 
            selectedMdiIntake.questionnaire_response_id || 
            selectedMdiIntake.responseId || 
            ''
          }
          intakeType={selectedMdiIntake.therapyCategory || 'general'}
          program={selectedMdiIntake.therapyCategory}
          clinicId={clinic.id}
          onSuccess={handleMdiSuccess}
        />
      )}
    </div>
  );
};

export default PatientIntakesTab;
